import React, { forwardRef } from 'react';
import { Text } from '@fragments';
import './styles.scss';

/**
 * Card — the elevated-surface primitive. Bakes the house recipe (soft gradient
 * fill, hairline ring, diffuse shadow, generous radius) into one shell with
 * optional header (eyebrow / title / subtitle / leading icon / trailing action)
 * and footer slots, so card examples compose instead of re-deriving the recipe.
 *
 * The header and footer are skipped entirely when their inputs are absent, so a
 * bare `<Card>{content}</Card>` is just a styled surface.
 *
 * @param {string}    [eyebrow]    Tiny UPPERCASE label above the title (Text.subheading).
 * @param {ReactNode} [title]      Card title (Text.h5, weight 500, strong).
 * @param {ReactNode} [subtitle]   Muted supporting line under the title.
 * @param {ReactNode} [icon]       Leading glyph, shown in a tinted square chip.
 * @param {ReactNode} [action]     Header-right slot — a Button, Menu trigger, "See all" link…
 * @param {ReactNode} [footer]     Footer slot (a hairline divider sits above it).
 * @param {boolean}   [interactive] Hover lift + pointer (for whole-card click targets).
 * @param {boolean}   [flat]       Flat surface fill instead of the soft gradient.
 * @param {boolean}   [headerDivider] Draw a hairline under the header.
 * @param {boolean}   [noBodyPadding] Remove body padding (for full-bleed lists/tables).
 * @param {() => void} [onClick]   Click handler; with `interactive` makes the card a button.
 * @param {string}    [className]
 * @param {ReactNode} children     Card body.
 */
const Card = forwardRef(function Card(
    {
        eyebrow,
        title,
        subtitle,
        icon,
        action,
        footer,
        interactive = false,
        flat = false,
        headerDivider = false,
        noBodyPadding = false,
        onClick,
        className = '',
        children,
        ...props
    },
    ref,
) {
    const hasHeader = !!(eyebrow || title || subtitle || icon || action);

    const interactiveProps = interactive
        ? {
              role: 'button',
              tabIndex: 0,
              onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClick?.(e);
                  }
              },
          }
        : {};

    const classes = [
        'card',
        interactive && 'card--interactive',
        flat && 'card--flat',
        headerDivider && 'card--header-divider',
        noBodyPadding && 'card--flush',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div ref={ref} className={classes} onClick={onClick} {...interactiveProps} {...props}>
            {hasHeader && (
                <div className="card__header">
                    {icon && <span className="card__icon">{icon}</span>}

                    <div className="card__heading">
                        {eyebrow && <Text.subheading contrast={6}>{eyebrow}</Text.subheading>}
                        {title && (
                            <Text.h5 weight={500} contrast={9} className="card__title">
                                {title}
                            </Text.h5>
                        )}
                        {subtitle && (
                            <Text.span contrast={6} className="card__subtitle">
                                {subtitle}
                            </Text.span>
                        )}
                    </div>

                    {action && <div className="card__action">{action}</div>}
                </div>
            )}

            <div className="card__body">{children}</div>

            {footer && <div className="card__footer">{footer}</div>}
        </div>
    );
});

export default Card;
